/**
 * AnalysisSummaryCard Component
 * 
 * Displays analysis metadata including row count, date range,
 * and positive assessments from the analysis.
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, FileText, Calendar, TrendingUp } from 'lucide-react';

interface DataSummary {
  row_count?: number;
  date_range?: {
    start?: string;
    end?: string;
  };
  categories_detected?: string[];
  total_inflow?: number;
  total_outflow?: number;
  negative_balance_runs?: Array<{ start: string; end: string; days: number }>;
  max_consecutive_negative_days?: number;
  late_fee_detected?: boolean;
  cost_interest_total?: number;
  interest_total?: number;
}

interface PositiveAssessment {
  pattern: string;
  description: string;
  confidence?: number;
}

interface AnalysisSummaryCardProps {
  dataSummary?: DataSummary | null;
  positiveAssessments?: PositiveAssessment[];
  findingsCount: number;
  className?: string;
}

export function AnalysisSummaryCard({
  dataSummary,
  positiveAssessments = [],
  findingsCount,
  className,
}: AnalysisSummaryCardProps) {
  const rowCount = dataSummary?.row_count ?? 0;
  const dateRange = dataSummary?.date_range;
  
  // Check if we have valid date range (not "okänt" or empty)
  const isValidDate = (dateStr: string | undefined): boolean => {
    if (!dateStr || dateStr === 'okänt' || dateStr === 'unknown') return false;
    const parsed = new Date(dateStr);
    return !isNaN(parsed.getTime());
  };
  
  const hasDateRange = isValidDate(dateRange?.start) && isValidDate(dateRange?.end);

  // Format date for display
  const formatDate = (dateStr: string | undefined): string => {
    if (!dateStr || dateStr === 'okänt' || dateStr === 'unknown') return '-';
    try {
      const parsed = new Date(dateStr);
      if (isNaN(parsed.getTime())) return '-';
      return parsed.toLocaleDateString('sv-SE');
    } catch {
      return '-';
    }
  };

  // Determine status badge
  const getStatusBadge = () => {
    if (findingsCount === 0) {
      return (
        <Badge variant="outline" className="bg-success/10 text-success border-success/30">
          <CheckCircle className="h-3 w-3 mr-1" />
          Inga avvikelser
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="bg-warning/10 text-warning border-warning/30">
        <TrendingUp className="h-3 w-3 mr-1" />
        {findingsCount} fynd
      </Badge>
    );
  };

  return (
    <Card className={`border-success/30 bg-success/5 ${className || ''}`}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <CheckCircle className="h-5 w-5 text-success" />
          Analyssammanfattning
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-3">
          {/* Row count */}
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-background">
              <FileText className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Analyserade rader</p>
              <p className="text-2xl font-bold">{rowCount.toLocaleString('sv-SE')}</p>
            </div>
          </div>

          {/* Date range */}
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-background">
              <Calendar className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Period</p>
              <p className="font-medium">
                {hasDateRange
                  ? `${formatDate(dateRange?.start)} → ${formatDate(dateRange?.end)}`
                  : 'Ej angiven'}
              </p>
            </div>
          </div>

          {/* Status */}
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-background">
              <TrendingUp className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Status</p>
              {getStatusBadge()}
            </div>
          </div>
        </div>

        {/* Positive assessments */}
        {positiveAssessments.length > 0 && (
          <div className="mt-4 pt-4 border-t border-success/20">
            <p className="text-sm font-medium mb-2">Positiva observationer:</p>
            <div className="space-y-2">
              {positiveAssessments.map((pa, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-2 text-sm text-success"
                >
                  <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>{pa.description || pa.pattern}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Default positive message when no findings and no assessments */}
        {findingsCount === 0 && positiveAssessments.length === 0 && (
          <div className="mt-4 pt-4 border-t border-success/20">
            <div className="flex items-start gap-2 text-sm text-success">
              <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>
                Analysen identifierade inga väsentliga avvikelser. 
                Data uppvisar normala mönster för perioden.
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
